/** Import react section **/
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { hasAuthorization } from '../../../store/session/sessionActions'
import queryString from 'query-string'
import { useLocation, useHistory } from 'react-router-dom'
import propTypes from 'prop-types'

/**Import Material UI section */
import { Button, Typography, Badge, Popper, Paper, ClickAwayListener } from '@material-ui/core'
import DownloadIcon from '@material-ui/icons/ArrowDropDown'

/**Import components sections */
import DateRangePicker from '../daterangepicker/DateRangePicker'
import ButtonCancel from '../Buttons/ButtonCancel'
import ButtonConfirm from '../Buttons/ButtonConfirm'
import defaultTheme from '../../../resources/themes/default'

/**Import resources section */
import { FilterSelectStyles } from '../../../resources/styles/ServicesListStyles'

const FilterDateRange = (props) => {
	const classes = FilterSelectStyles()
	let location = useLocation()
	let history = useHistory()
	const { nameFilter, startDateParam, endDateParam, userProfile, roles } = props
	const [settingsEl, setSettingsEl] = useState(null)
	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)
	const [isActive, setIsActive] = useState(false)

	/** Sync dates from URL on load/change */
	useEffect(() => {
		const queryParams = queryString.parse(location.search)
		const start = queryParams[startDateParam]
		const end = queryParams[endDateParam]
		if (start || end) {
			setStartDate(start ? moment(start) : null)
			setEndDate(end ? moment(end) : null)
			setIsActive(true)
		} else {
			setStartDate(null)
			setEndDate(null)
			setIsActive(false)
		}
	}, [location.search, startDateParam, endDateParam])

	const handleMenuOpen = (event) => {
		setSettingsEl(event.currentTarget)
		if (!startDate) setStartDate(moment().startOf('year'))
		if (!endDate) setEndDate(moment().startOf('day'))
	}

	const onHandleClose = () => {
		setSettingsEl(null)
	}

	const onDateRangeChange = (range) => {
		setStartDate(range.startDate)
		setEndDate(range.endDate)
	}

	/** Apply date range to URL */
	const onApply = () => {
		let queryParams = queryString.parse(location.search)
		if (startDate) queryParams[startDateParam] = startDate.format('YYYY-MM-DD')
		if (endDate) queryParams[endDateParam] = endDate.format('YYYY-MM-DD')
		history.push({ search: queryString.stringify(queryParams, { encode: false }) })
		setSettingsEl(null)
	}

	/** Clear date range from URL */
	const onCancel = () => {
		let queryParams = queryString.parse(location.search)
		delete queryParams[startDateParam]
		delete queryParams[endDateParam]
		history.push({ search: queryString.stringify(queryParams, { encode: false }) })
		setStartDate(null)
		setEndDate(null)
		setSettingsEl(null)
	}

	const predefinedRanges = [
		{ label: 'This Year', startDate: moment().startOf('year'), endDate: moment() },
		{ label: 'Last Year', startDate: moment().add(-1, 'year'), endDate: moment() },
		{ label: 'Last Semester', startDate: moment().add(-6, 'month'), endDate: moment() },
		{ label: 'Last Trimester', startDate: moment().add(-3, 'month'), endDate: moment() },
	]

	let isAuthorizer = hasAuthorization(userProfile, roles)
	let open = !!settingsEl

	return (
		<div className={classes.root}>
			{isAuthorizer && (
				<Badge badgeContent={isActive ? 1 : 0} color='error'>
					<Button
						endIcon={<DownloadIcon />}
						size='small'
						onClick={handleMenuOpen}
						className={!isActive ? classes.buttonFilter : classes.buttonFilterSelected}
					>
						<Typography variant='subtitle2'>{nameFilter}</Typography>
					</Button>
				</Badge>
			)}
			<Popper anchorEl={settingsEl} open={open} placement={'bottom'}>
				<ClickAwayListener onClickAway={onHandleClose}>
					<Paper elevation={2} className={classes.papperPopper}>
						<DateRangePicker
							textColor={defaultTheme.palette.primary.light}
							variant='outlined'
							startDate={startDate || moment().startOf('year')}
							endDate={endDate || moment()}
							predefinedRanges={predefinedRanges}
							onChangeRange={onDateRangeChange}
						/>
						<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', gap: '8px' }}>
							<ButtonCancel title='CLEAR' onCancelClick={onCancel} isSaving={false} />
							<ButtonConfirm title='APPLY' onSavedClick={onApply} isSaving={false} />
						</div>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}

FilterDateRange.propTypes = {
	userProfile: propTypes.shape({
		userName: propTypes.string.isRequired,
		name: propTypes.string.isRequired,
		email: propTypes.string.isRequired,
		avatarUrl: propTypes.string,
		enabled: propTypes.bool.isRequired,
		roles: propTypes.array,
	}),
	nameFilter: propTypes.string.isRequired,
	startDateParam: propTypes.string.isRequired,
	endDateParam: propTypes.string.isRequired,
	roles: propTypes.array,
}

FilterDateRange.defaultProps = {
	nameFilter: 'Date Range',
	startDateParam: 'startDate',
	endDateParam: 'endDate',
	roles: [],
}

export default FilterDateRange
